import {
  ExperienceAnalysisQueryTemplateType,
  OpenAiMessage,
  OpenAiRoleType,
} from "@intelligems/ig-types";

import { ExperienceFe } from "../../types/schemas/experiences";
import { AiAssistants, initialAiState } from "../slices/ai";
import { AiExperienceAnalysisFe } from "../types/ai";

export type ExperienceAnalysisReducerAction =
  | { type: "reset" }
  | { type: "back" }
  | {
      type: "setDataProgress";
      value: number;
    }
  | {
      type: "setQuestion";
      value: string;
    }
  | {
      type: "setIsHidden";
      value: boolean;
    }
  | {
      type: "setAutoScroll";
      value: boolean;
    }
  | {
      type: "setViewPreviousThreads";
      value: boolean;
    }
  | {
      type: "sendQuery";
      value: string;
    }
  | {
      type: "setCreatedAt";
      value: number;
    }
  | {
      type: "setExperience";
      value: ExperienceFe;
    }
  | {
      type: "setQueryState";
      value: {
        queryTemplate?: ExperienceAnalysisQueryTemplateType;
      };
    }
  | {
      type: "setAnalyzingData";
      value: {
        threadId: string;
        createdAt: number;
      };
    }
  | {
      type: "setThreadFromHistory";
      value: {
        threadId: string;
        assistantId: string;
        createdAt: number;
        messages: OpenAiMessage[];
      };
    }
  | {
      type: "resultsDone";
    }
  | {
      type: "custom";
      message: string;
    }
  | {
      type: "resultsDelta";
      value: {
        messageId: string;
        delta: string;
        role: OpenAiRoleType;
        messageType?: "file";
      };
    };

export function ExperienceAnalysisReducer(
  draft: AiExperienceAnalysisFe,
  action: ExperienceAnalysisReducerAction
) {
  switch (action.type) {
    case "reset":
      draft = initialAiState.experienceAnalysis;
      return draft;
    case "back":
      draft = {
        ...initialAiState.experienceAnalysis,
        isHidden: false,
      };
      return draft;
    case "setIsHidden":
      draft.isHidden = action.value;
      return draft;
    case "setAutoScroll":
      draft.autoScroll = action.value;
      return draft;
    case "setCreatedAt":
      draft.createdAt = action.value;
      return draft;
    case "setQuestion":
      draft.query = action.value;
      return draft;
    case "setViewPreviousThreads":
      draft.viewPreviousThreads = action.value;
      return draft;
    case "sendQuery":
      draft.query = action.value;
      draft.queryTemplate = "custom";
      draft.assistantId = AiAssistants.custom;
      draft.isReadyForQuery = false;
      draft.isMessageComplete = false;
      draft.dataProgress = 0;

      const userMessages = draft.messages.filter((m) => m.role === "user");
      draft.messages.push({
        role: "user",
        message: action.value,
        id: `user-msg-${userMessages.length + 1}`,
      });

      return draft;
    case "setExperience":
      draft.experience = action.value;
      return draft;
    case "setQueryState":
      draft.dataProgress = 0;
      draft.isHidden = false;

      if (action.value.queryTemplate) {
        draft.queryTemplate = action.value.queryTemplate;

        if (action.value.queryTemplate === "summary") {
          draft.isReadyForQuery = false;
          draft.isMessageComplete = false;
          draft.step = "pendingGatheringData";
        } else if (action.value.queryTemplate === "custom") {
          draft.messages = [];
          draft.isReadyForQuery = true;
          draft.step = "resultsDone";
        }
      }
      return draft;
    case "setDataProgress":
      draft.step = "pendingGatheringData";
      if (action.value > draft.dataProgress) {
        draft.dataProgress = action.value;
      }
      return draft;
    case "setThreadFromHistory":
      draft.threadId = action.value.threadId;
      draft.assistantId = action.value.assistantId;
      draft.messages = action.value.messages;
      draft.viewPreviousThreads = false;
      draft.step = "resultsDone";
      draft.createdAt = action.value.createdAt;
      draft.queryTemplate = "custom";
      draft.isReadyForQuery = true;
      draft.isMessageComplete = true;
      return draft;
    case "setAnalyzingData":
      draft.step = "pendingAnalyzingData";
      draft.threadId = action.value.threadId;
      draft.createdAt = action.value.createdAt;
      return draft;
    case "resultsDelta":
      draft.step = "resultsDelta";
      const index = draft.messages.findIndex(
        (message) => message.id === action.value.messageId
      );
      if (index !== -1) {
        draft.messages[index].message =
          draft.messages[index].message + action.value.delta;
      } else {
        draft.messages.push({
          id: action.value.messageId,
          message: action.value.delta,
          role: action.value.role,
        });
      }
      return draft;
    case "resultsDone":
      draft.step = "resultsDone";
      draft.isMessageComplete = true;
      draft.isReadyForQuery = true;
      draft.query = "";
      return draft;
    case "custom":
      draft.step = "custom";
      draft.messages.push({
        message: action.message,
        role: "assistant",
        id: `assistant-msg-${draft.messages.length + 1}`,
      });
      return draft;
    default:
      return draft;
  }
}
